import { t as translate } from "./preferences";
import type { PaymentMethod, WorkCreateInput } from "./api";
import type { DraftPayment, WorkDraft } from "./workDraft";
import { quantityNumber } from "./quantity";

export function paymentLines(draft: WorkDraft): DraftPayment[] {
  return draft.payments ?? [{ method: draft.paymentMethod, amount: draft.paid }];
}

export function togglePaymentMethod(
  lines: DraftPayment[],
  method: PaymentMethod,
  total: number,
): DraftPayment[] {
  if (lines.some((line) => line.method === method)) {
    return lines.filter((line) => line.method !== method);
  }
  const materialized = lines.length === 1 && lines[0].amount === "" && !lines[0].amountEdited
    ? [{ ...lines[0], amount: String(total) }]
    : lines;
  return [...materialized, { method, amount: "" }];
}

export function integerMoney(value: string): number | null {
  const normalized = value.replace(/[\s\u00a0\u202f]/g, "");
  if (!/^\d+$/.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

export function workTotal(draft: WorkDraft): number {
  const serviceLines = draft.services ?? (
    draft.serviceId ? [{ price: draft.price }] : []
  );
  const service = serviceLines.reduce(
    (sum, item) => sum + (integerMoney(item.price) ?? 0),
    0,
  );
  return service + draft.parts.reduce(
    (sum, part) => sum + quantityNumber(part.quantity) * (integerMoney(part.price) ?? 0),
    0,
  );
}

export type PaymentResolution = {
  paid: number;
  debt: number;
  fields: Pick<WorkCreateInput, "tolangan_summa" | "debt_due_days" | "tolov_usuli" | "payments">;
  error: string | null;
};

export function resolvePayment(
  draft: WorkDraft,
  hasDebts: boolean,
): PaymentResolution {
  const total = workTotal(draft);
  const lines = draft.payments ?? [{ method: draft.paymentMethod, amount: draft.paid }];
  if (!hasDebts) {
    return {
      paid: total,
      debt: 0,
      fields: total > 0 ? {
        tolov_usuli: lines[0]?.method ?? draft.paymentMethod,
        payments: [{ tolov_usuli: lines[0]?.method ?? draft.paymentMethod, summa: String(total) }],
      } : { payments: [] },
      error: null,
    };
  }
  if (lines.length > 1 && lines.some((line) => integerMoney(line.amount) === null || integerMoney(line.amount)! <= 0)) {
    return { paid: 0, debt: total, fields: {}, error: translate("Tanlangan har bir to'lov usuliga summa kiritilishi shart.") };
  }
  const amounts = lines.map((line) => (
    lines.length === 1 && line.amount === "" ? (line.amountEdited ? 0 : total) : integerMoney(line.amount)
  ));
  if (amounts.some((amount) => amount === null)) {
    return { paid: 0, debt: total, fields: {}, error: translate("ToвЂlangan summani toвЂgвЂri kiriting.") };
  }
  const paid = amounts.reduce<number>((sum, amount) => sum + (amount ?? 0), 0);
  if (paid === null) return { paid: 0, debt: total, fields: {}, error: translate("To‘langan summani to‘g‘ri kiriting.") };
  if (paid > total) return { paid, debt: 0, fields: {}, error: translate("To‘langan summa jami summadan oshmasligi kerak.") };
  const debt = total - paid;
  const fields: PaymentResolution["fields"] = {
    tolangan_summa: lines.length === 1 && lines[0].amount === "" && !lines[0].amountEdited ? null : String(paid),
    payments: lines.flatMap((line, index) => {
      const value = amounts[index] ?? 0;
      return value > 0 ? [{ tolov_usuli: line.method, summa: String(value) }] : [];
    }),
  };
  if (debt > 0) {
    const days = integerMoney(draft.debtDueDays);
    if (days === null || days < 1 || days > 3650) {
      return { paid, debt, fields, error: translate("Qarz muddati 1 dan 3650 kungacha bo‘lishi kerak.") };
    }
    fields.debt_due_days = days;
  }
  if (paid > 0 && fields.payments?.length === 1) {
    fields.tolov_usuli = fields.payments[0].tolov_usuli as PaymentMethod;
  }
  return { paid, debt, fields, error: null };
}
